// Environment specific values this package needs at runtime. The host application owns
// its environment, so it injects these once at bootstrap and `@acre/design-system` imports
// no environment module of its own — which is what keeps other environments' hosts and
// credentials out of the published bundle.
export interface DesignSystemConfig {
  /** Base URL of the Acre API, used to name the environment analytics events are tagged with. */
  apiUrl: string
  /** Project token the MixPanel browser SDK is initialised with. */
  mixpanelToken: string
  /** Sentry environment name, which also identifies whether this deployment is production. */
  sentryEnvironment: string
  /** Host of the Matomo instance page views are reported to. Absent disables Matomo. */
  matomoBaseUrl?: string
  /** Matomo site the page views are recorded against. Only meaningful with a base URL. */
  matomoSiteId?: string
}

// Keys with no meaningful absent value. Reading one before it is set is a defect — the
// alternative is silently talking to the wrong host, or treating production as a debug
// build — so it throws rather than defaults.
type RequiredConfigKey = 'apiUrl' | 'mixpanelToken' | 'sentryEnvironment'

let injectedConfig: Readonly<DesignSystemConfig> | undefined

/**
 * Injects the runtime configuration. Call once from the host application's entry point,
 * before anything renders or issues a request.
 */
export const setDesignSystemConfig = (config: DesignSystemConfig) => {
  injectedConfig = Object.freeze({ ...config })
}

const readRequired = (key: RequiredConfigKey) => {
  const value = injectedConfig?.[key]

  if (!value) {
    throw new Error(
      `@acre/design-system has no runtime configuration for "${key}". Call setDesignSystemConfig() with the host application's environment configuration from its entry point before this code runs.`,
    )
  }

  return value
}

export const getApiUrl = () => readRequired('apiUrl')

export const getMixpanelToken = () => readRequired('mixpanelToken')

export const getSentryEnvironment = () => readRequired('sentryEnvironment')

// A deployment that configures no Matomo host reports no page views, which is a valid
// state — the caller skips tracking entirely — so an absent value reads as empty rather
// than throwing.
export const getMatomoBaseUrl = () => injectedConfig?.matomoBaseUrl ?? ''

// The host supplies the site id as an environment string; Matomo needs a number. Only
// meaningful alongside a base URL, so an absent value parses to `NaN` and the caller's
// base URL guard keeps it unread.
export const getMatomoSiteId = () => Number.parseInt(injectedConfig?.matomoSiteId ?? '', 10)
